'use strict';
/**
 * Basic utilities module.
 * @module utils/basic
 */

/**
 * Oneliner for Object.defineProperty() using simple `value` descriptor.
 * @param {Object} object The object on which to define the property.
 * @param {string} property The name of the property to be defined or modified.
 * @param {*} value The value associated with the property. Can be any valid JavaScript value (number, object, function, etc).
 */
function definePrivateProperty(object, property, value) {
    Object.defineProperty(object, property, {
        value: value,
    });
}

/**
 * TODO description.
 */
function defineProperties(handler, properties) {
    var propertiesNames = Object.keys(properties);
    /* jshint loopfunc: false */
    propertiesNames.forEach(function (key) {
        Object.defineProperty(handler.prototype, key, {
            get: function () {
                return properties[key];
            },
            enumerable: true
        });
    });

    return propertiesNames.map(function (key) {
        return properties[key];
    });
}

/**
 * Formats a message replacing placeholders with variables values.
 * @param {string} message - A message that can contain placeholders.
 * @param {Object} [variables] - Variables for replacing message placeholders.
 *
 * @example
 * formatMessage("Hello {name}", {name: "John"});
 * // > "Hello John"
 *
 * formatMessage("Hello {{name}}", {name: "John"});
 * // > "Hello \"John\""
 */
function formatMessage(message, variables) {
    return message
        // Replace {{variable}} with `JSON.stringify(variableValue)`.
        .replace(/{{([0-9a-zA-Z-_. ]+)}}/g, function (_, variable) {
            // JSON.stringify(NaN) yields 'null', so we need a special case for NaN
            if (typeof variables[variable] === 'number' && isNaN(variables[variable])) {
                return 'NaN';
            }
            return JSON.stringify(variables[variable]);
        })
        // Replace {variable} with `variableValue`.
        .replace(/{([0-9a-zA-Z-_. ]+)}/g, function (_, variable) {
            return variables[variable];
        });
}

/**
 * Checks if passed *object* is a plain object.
 * @param {*} object - Value subject to test.
 * @returns {boolean} Returns `true` if *object* is a plain object or `false`.
 *
 * @example
 *
 * isPlainObject(); // > false
 * isPlainObject(null); // > false
 * isPlainObject(new Date()); // > false
 * isPlainObject([]); // > false
 * isPlainObject(1); // > false
 * isPlainObject(true); // > false
 * isPlainObject("foo"); // > false
 * isPlainObject(function() {}); // > false
 * isPlainObject({a: 1}); // > true
 */
function isPlainObject(object) {
    // Inspired by jQuery Core, but reduced to our use case.
    return object !== null &&
        '' + object === '[object Object]' &&
        object.constructor === Object;
}

/**
 * @param {Array} array - The list of strings to glue together as an English formatted list string.
 * @param {string} conjunction - Conjunction value, e.g., `'and'`, `'or'`.
 * @returns {string} Returns an English formatted list string using the passed *conjunction*.
 *
 * @example
 *
 * listFormat([], 'or'); // > ''
 * listFormat(['foo'], 'or'); // > 'foo'
 * listFormat(['foo', 'bar'], 'or'); // > 'foo or bar'
 * listFormat(['foo', 'bar', 'baz'], 'or'); // > 'foo, or bar, or baz'
 */
function listFormat(array, conjunction) {
    if (array.length === 0) {
        return '';
    }
    if (array.length === 1) {
        return array[0];
    }
    if (array.length === 2) {
        return array.join(' ' + conjunction + ' ');
    }
    if (array.length > 2) {
        return array.join(', ' + conjunction + ' ');
    }
}

/**
 * Returns the type of a variable with additional types than native `typeof`.
 * @param {*} variable - A variable to deduce its type.
 *
 * @example
 * smarterTypeof(null); // > "null"
 * smarterTypeof({a: 1}); // > "plain object"
 * smarterTypeof([]); // > "array"
 * smarterTypeof(new Date()); // > "date"
 * smarterTypeof(<anything else>); // > result of regular typeof.
 */
function smarterTypeof(variable) {
    if (variable === null) {
        return 'null';
    }
    if (isPlainObject(variable)) {
        return 'plain object';
    }
    if (Array.isArray(variable)) {
        return 'array';
    }
    if (variable instanceof Date) {
        return 'date';
    }
    return typeof variable;
}

/**
 * Returns `true` is *element* belongs to *collection*.
 * @param {*} element - The key of an Object or the element of an Array.
 * @param {(Array| Object)} Where to search for element at.
 *
 * @example
 *
 * withinRange("bar", ["foo", "bar", "baz"]);
 * // > true
 * withinRange("quax", ["foo", "bar", "baz"]);
 * // > false
 *
 * @example
 *
 * withinRange("bar", {foo: 1, bar: 2, baz: 3});
 * // > true
 * withinRange("quax", {foo: 1, bar: 2, baz: 3});
 * // > false
 */
function withinRange(element, collection) {
    return Array.isArray(collection) ?
        collection.indexOf(element) !== -1 :
        element in collection;
}

/**
 * Given a value and a default for that value,
 * if value is undefined, return the default.
 * Otherwise, return the value itself.
 * @param {*} value the value which may or may not be undefined
 * @param {*} defaultValue the default for value
 *
 * @example
 *
 * getValueOrDefault('my string', 'default string');
 * // > 'my string'
 * getValueOrDefault(undefined, 'default string');
 * // > 'default string'
 */
function getValueOrDefault(value, defaultValue) {
    return value === undefined ? defaultValue : value;
}

module.exports = {
    definePrivateProperty: definePrivateProperty,
    defineProperties: defineProperties,
    formatMessage: formatMessage,
    getValueOrDefault: getValueOrDefault,
    isPlainObject: isPlainObject,
    listFormat: listFormat,
    smarterTypeof: smarterTypeof,
    withinRange: withinRange
};



//////////////////
// WEBPACK FOOTER
// /donatenodeweb-ci-2793/workspace/donate-gro-release/~/griffin-paypal/lib/client/utils/basic.js
// module id = 307
// module chunks = 6